import { colors } from '@kmns-internal/furikake'
import { Divider, Stack, TextField, Typography } from '@mui/material'
import {
  type AddPlaceV2Request,
  type GetPlacesRequest,
  type PlaceNode,
  PlaceNodeTypeEnum,
  type UpdatePlaceV2Request,
} from '@ulysses-inc/harami_api_client'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { UUID } from 'src/exShared/util/uuid'
import Yup from 'src/features/validation/yup'
import placesOperations from 'src/state/ducks/places/operations'
import type { RootState } from 'src/state/store'
import FooterButton from 'src/views/components/drawer/FooterButton'

interface StateProps {
  activePlace?: PlaceNode
  isLoadingPlace: boolean
  request: GetPlacesRequest
}

interface DispatchProps {
  changeIsShowEditPlaceDrawer: (isShow: boolean) => void
  updatePlace: (request: UpdatePlaceV2Request) => void
  addPlaceNode: (request: AddPlaceV2Request) => void
  changeUpdatedPlacesFlag: (edited: boolean) => void
}

type EditPlaceProps = StateProps & DispatchProps

interface PlaceForm {
  nodeId?: string
  name: string
}

const EditPlace = () => {
  // react-reduxのconnect関数の利用をやめた際のなごり
  // (今後このコンポーネントを触ることがあればそのときに解体する)
  const exProps = useProps()

  const { handleChange, handleSubmit, values, errors } = useFormik<PlaceForm>({
    initialValues: {
      nodeId: exProps.activePlace?.uuid || undefined,
      name: exProps.activePlace?.place?.name || '',
    },
    validationSchema: validationSchema,
    onSubmit: (form, formikHelpers) => {
      const placeNode = {
        id: exProps.activePlace ? exProps.activePlace.id : 0,
        uuid: exProps.activePlace ? exProps.activePlace.uuid : UUID(),
        type: PlaceNodeTypeEnum.Place,
        isRootNode: exProps.activePlace ? exProps.activePlace.isRootNode : 0,
        place: {
          id: exProps.activePlace?.place ? exProps.activePlace.place.id : 0,
          name: form.name,
        },
        parentNodes: exProps.activePlace ? exProps.activePlace.parentNodes : [],
        nodes: exProps.activePlace ? exProps.activePlace.nodes : [],
      }

      if (exProps.activePlace !== undefined) {
        exProps.updatePlace({
          placeNodeId: placeNode.uuid,
          placeNode: placeNode,
        })
      } else {
        exProps.addPlaceNode({
          placeNode: placeNode,
        })
      }
      formikHelpers.resetForm()
    },
  })

  return (
    <Stack sx={{ minHeight: '100vh' }}>
      <Typography variant="h6" sx={{ my: 2, px: 3 }}>
        {exProps.activePlace ? '現場の編集' : '現場の追加'}
      </Typography>
      <Divider />
      <Stack>
        <Stack sx={{ px: 3, py: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            現場名
          </Typography>
          <TextField
            fullWidth
            size="medium"
            onChange={handleChange('name')}
            defaultValue={exProps.activePlace?.place?.name ?? ''}
            value={values.name}
            error={Boolean(errors.name)}
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
          <Typography variant="body1" color={colors.error.main}>
            {errors.name}
          </Typography>
        </Stack>
        <Stack sx={{ px: 3, py: 2 }}>
          <FooterButton
            spinning={exProps.isLoadingPlace}
            onCancel={() => exProps.changeIsShowEditPlaceDrawer(false)}
            onSubmit={() => {
              handleSubmit()
              exProps.changeUpdatedPlacesFlag(true)
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}

const useProps = (): EditPlaceProps => {
  const dispatch = useDispatch()
  const state = useSelector((state: RootState) => state)

  const activePlaceId = state.placesState.places.activePlaceId

  return {
    // -----------------------------------
    // StateProps
    // -----------------------------------

    activePlace: activePlaceId
      ? state.placesState.places.places.find(
          place => place.uuid === activePlaceId,
        )
      : undefined,
    isLoadingPlace: state.placesState.places.isLoading,
    request: state.placesState.places.request,

    // -----------------------------------
    // DispatchProps
    // -----------------------------------

    addPlaceNode: (request: AddPlaceV2Request) => {
      placesOperations.addPlace(dispatch, request)
    },
    updatePlace: (request: UpdatePlaceV2Request) => {
      placesOperations.updatePlace(dispatch, request)
    },
    changeIsShowEditPlaceDrawer: (isShow: boolean) => {
      placesOperations.changeIsShowEditPlaceDrawer(dispatch, isShow)
    },
    changeUpdatedPlacesFlag: (edited: boolean) => {
      placesOperations.changeUpdatedPlacesFlag(dispatch, edited)
    },
  }
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required().max(255).label('現場名'),
})

export default EditPlace
